import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import moment from 'moment';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../chart/bodygraph/hd-content-language.service';
import { getHdPlanetDisplayConfig } from '../../chart/bodygraph/planet-grid/hd-planet-display-config';
import { formatGateLine } from '../transit-countdown.utils';
import { hdSeasonBySunGate } from '../../chart/mandala/hd-season-markers';
import { HdTransitLineDay, HdTransitLineDayPart } from './hd-transit-day-flow.builder';

const I18N_PREFIX = 'humanDesign.chart.transits.vertical.sunLine';
const SUN = getHdPlanetDisplayConfig('sun');

/**
 * Lijn-dag van de zon (Steven 2026-10-21): de lijn waarin de zon die dag
 * staat, rechtsboven in een dagtegel van de weekstrip en in een dagcel van de
 * maandkalender. Zon-glyph in de planeetkleur, het lijnnummer in donker goud:
 * zo leest het nooit als een dagnummer. Wisselt de zon die dag van lijn (bijna
 * elke dag, een zonlijn duurt ±22 à 24 uur), dan staan alle lijnen van die dag
 * er chronologisch, de lijn die het grootste deel van de dag actief is iets
 * groter. De tooltip noemt de standen met hun uren.
 *
 * Smalle tegels/cellen (rechterpaneel, telefoon, smalle kalender) passen het
 * label aan met container queries op `hd-week-day` (weekstrip) en
 * `hd-calendar-day` (kalender), per aantal lijnen (`data-parts`): zie de scss.
 * De tooltip blijft altijd volledig.
 */
@Component({
  selector: 'hd-transit-sun-line',
  templateUrl: './hd-transit-sun-line.component.html',
  styleUrl: './hd-transit-sun-line.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'img',
    '[attr.aria-label]': 'label()',
    '[attr.title]': 'label()',
    '[attr.data-parts]': 'day().parts.length',
    '[attr.data-marker]': 'marker()?.kind ?? null',
  },
})
export class HdTransitSunLineComponent {
  readonly day = input.required<HdTransitLineDay>();

  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  protected readonly glyph = SUN.astronomiconChar;
  protected readonly glyphColor = SUN.stripeColor;

  private readonly lang = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  /**
   * Seizoens- of kwartaalmarkering van de dag (lecture 2026-10-21, analyse 4C):
   * een zonnewende of equinox krijgt het seizoensicoon, de dag waarop de zon een
   * Sphinx-poort ingaat (nieuw kwartaal van het wiel) een vlaggetje. Hooguit één
   * per dag; het seizoen wint.
   */
  protected readonly marker = computed<{ kind: 'season' | 'quarter'; icon: string } | null>(() => {
    const day = this.day();
    if (day.season != null) {
      return { kind: 'season', icon: hdSeasonBySunGate(day.season.sunGate)?.icon ?? 'sun' };
    }
    if (day.quarterStart != null) {
      return { kind: 'quarter', icon: 'flag' };
    }
    return null;
  });

  /**
   * Tooltip en toegankelijke naam: één regel bij een hele dag, anders de dominante
   * lijn + per lijn de uren; daarna het seizoensmoment of de kwartaalstart van de dag.
   */
  protected readonly label = computed(() => {
    const lang = this.lang();
    const translate = (key: string, params: Record<string, string | number>) =>
      this.transloco.translate<string>(`${I18N_PREFIX}.${key}`, params, lang);
    const day = this.day();
    const parts = day.parts;
    const lines: string[] = [];
    if (parts.length === 1) {
      lines.push(translate('allDay', { line: parts[0].line, gateLine: formatGateLine(parts[0].gate, parts[0].line) }));
    } else {
      const dominant = parts.find(part => part.dominant) ?? parts[0];
      lines.push(
        translate('mostly', { line: dominant.line }),
        ...parts.map(part => translate('part', {
          from: moment(part.fromMs).format('HH:mm'),
          to: endTimeText(part),
          line: part.line,
          gateLine: formatGateLine(part.gate, part.line),
        })),
      );
    }
    if (day.season != null) {
      lines.push(translate('season', {
        season: this.transloco.translate<string>(`humanDesign.chart.mandala.seasons.${day.season.key}`, undefined, lang),
        time: moment(day.season.atMs).format('HH:mm'),
        sunGateLine: formatGateLine(day.season.sunGate, day.season.sunLine),
        earthGateLine: formatGateLine(day.season.earthGate, day.season.sunLine),
      }));
    }
    if (day.quarterStart != null) {
      lines.push(translate('quarterStart', {
        quarter: this.transloco.translate<string>(`humanDesign.chart.mandala.quarters.${day.quarterStart.key}.title`, undefined, lang),
        time: moment(day.quarterStart.atMs).format('HH:mm'),
        gate: day.quarterStart.gate,
      }));
    }
    return lines.join('\n');
  });
}

/** Het einde van de dag (volgende lokale middernacht) leest als 24:00, niet als 00:00. */
function endTimeText(part: HdTransitLineDayPart): string {
  const text = moment(part.toMs).format('HH:mm');
  return text === '00:00' ? '24:00' : text;
}
